import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiApi } from '@/api/ops';
import type { AiLocalTariff, AiRateCard } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { StatusChip } from '@/components/ops/Badges';
import { ReasonField, TextField, reasonOk } from '@/components/ops/Forms';
import { Section } from '@/components/ops/Layout';
import { ConfirmDialog, Modal } from '@/components/ops/Modal';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatCredits, isValidScale, parseDisplayedCredits, scaleText } from '@/ops/credits';
import { formatBps, formatTimestamp } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { scaleUnavailableReason, useCreditScale } from '@/ops/useCreditScale';

const aiKey = (env: string) => ['ops', 'ai', env, 'rate-cards'] as const;

/** A non-negative decimal typed by an admin (`2.5`, `0.15`); null for empty or anything else. */
export function parseDecimal(text: string): number | null {
  const t = text.trim().replace(/,/g, '');
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** A percentage typed as `20` / `20.5` → basis points; null when empty or invalid (Number('') is 0 — never that). */
export function percentToBps(text: string): number | null {
  const n = parseDecimal(text);
  return n === null ? null : Math.round(n * 100);
}

/**
 * Displayed credits per unit → micro-credits per unit for a LOCAL TARIFF, which ai stores as a decimal (a tariff may be a
 * fraction of a micro-credit per token). Up to 6 decimals of a micro-credit; null when the scale is unknown or the text is
 * not a non-negative number.
 */
export function displayedRateToMicro(text: string, scale: number | null): number | null {
  if (!isValidScale(scale)) return null;
  const n = parseDecimal(text);
  return n === null ? null : Number((n * scale).toFixed(6));
}

/**
 * Pricing ▸ AI (money review C6): the platform AI rate cards (cloud provider × model: our USD cost per million units, the
 * margin, the minimum charge) and the local-engine tariffs, read live from anyware.ai through the console's AI proxy. Rows are
 * versions: "add a version" makes the new one active (the previous active row of that provider + model, or task, is
 * deactivated with it), "deactivate" retires one. Every write needs a reason and is audited by ai.
 */
export function AiRatesPanel({ env }: { env: string }) {
  const rates = useQuery({ queryKey: aiKey(env), queryFn: () => aiApi.rateCards(env), ...LIVE });
  const { scale, status } = useCreditScale(env);
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [adding, setAdding] = useState<'card' | 'tariff' | null>(null);
  const [deactivating, setDeactivating] = useState<{ kind: 'card'; row: AiRateCard } | { kind: 'tariff'; row: AiLocalTariff } | null>(null);

  return (
    <RequirePermission permission={PERMISSIONS.costsRead} what="AI rate cards">
      <QueryState query={rates} service="ai">
        {(data) => {
          const cards = [...data.cards].sort((a, b) => Number(b.active) - Number(a.active) || a.provider_key.localeCompare(b.provider_key)
            || a.model_key.localeCompare(b.model_key) || b.created_at.localeCompare(a.created_at));
          const tariffs = [...data.local_tariffs].sort((a, b) => Number(b.active) - Number(a.active) || a.task.localeCompare(b.task)
            || b.created_at.localeCompare(a.created_at));
          return (
            <div className="stack">
              <p className="t-caption muted" style={{ margin: 0 }}>
                What every tenant pays for AI: a cloud call costs our provider price (USD per million units) plus the margin, never
                less than the minimum charge; a local-engine task costs its tariff. 1 credit = {scaleText(scale)} micro-credits.
              </p>
              <Section title="Cloud rate cards" meta="one active version per provider + model"
                actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding('card')}>Add a version</button>} flush>
                {cards.length === 0 ? <div className="empty-state t-body">No rate cards — a cloud call without one is priced from the last known card or refused.</div> : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead><tr><th>Provider · model</th><th className="num">Input / output (USD per million)</th><th className="num">Margin</th><th className="num">Minimum</th><th>Version</th><th>State</th>{canManage && <th aria-label="Actions" />}</tr></thead>
                      <tbody>
                        {cards.map((c) => (
                          <tr key={c.id} className={c.active ? undefined : 'row-muted'}>
                            <td className="mono">{c.provider_key}<div className="t-caption muted">{c.model_key} · per {c.unit}</div></td>
                            <td className="num">{c.input_cost_per_million_usd} / {c.output_cost_per_million_usd}</td>
                            <td className="num">{formatBps(c.margin_bps)}</td>
                            <td className="num">{formatCredits(c.min_charge_credits, scale)}</td>
                            <td className="t-caption">{c.version}<div className="muted">{formatTimestamp(c.created_at)}</div></td>
                            <td>{c.active ? <StatusChip status="active" tone="success" /> : <StatusChip status="inactive" tone="muted" />}</td>
                            {canManage && (
                              <td>{c.active && (
                                <button type="button" className="btn btn-ghost btn-sm is-danger" aria-label={`Deactivate ${c.provider_key} ${c.model_key}`}
                                  onClick={() => setDeactivating({ kind: 'card', row: c })}>Deactivate</button>
                              )}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              <Section title="Local-engine tariffs" meta="one active version per task"
                actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding('tariff')}>Add a version</button>} flush>
                {tariffs.length === 0 ? <div className="empty-state t-body">No local tariffs.</div> : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead><tr><th>Task</th><th className="num">Per unit</th><th className="num">Minimum</th><th>Version</th><th>State</th>{canManage && <th aria-label="Actions" />}</tr></thead>
                      <tbody>
                        {tariffs.map((t) => (
                          <tr key={t.id} className={t.active ? undefined : 'row-muted'}>
                            <td className="mono">{t.task}<div className="t-caption muted">per {t.unit}</div></td>
                            <td className="num">{formatCredits(t.credits_per_unit, scale)}<div className="t-caption muted">{t.credits_per_unit.toLocaleString('en-US', { maximumFractionDigits: 6 })} µ</div></td>
                            <td className="num">{formatCredits(t.min_charge_credits, scale)}</td>
                            <td className="t-caption">{t.version}<div className="muted">{formatTimestamp(t.created_at)}</div></td>
                            <td>{t.active ? <StatusChip status="active" tone="success" /> : <StatusChip status="inactive" tone="muted" />}</td>
                            {canManage && (
                              <td>{t.active && (
                                <button type="button" className="btn btn-ghost btn-sm is-danger" aria-label={`Deactivate ${t.task}`}
                                  onClick={() => setDeactivating({ kind: 'tariff', row: t })}>Deactivate</button>
                              )}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {adding === 'card' && <RateCardForm env={env} scale={scale} scaleStatus={status} onClose={() => setAdding(null)} />}
              {adding === 'tariff' && <TariffForm env={env} scale={scale} scaleStatus={status} onClose={() => setAdding(null)} />}
              {deactivating && <DeactivateDialog env={env} target={deactivating} onClose={() => setDeactivating(null)} />}
            </div>
          );
        }}
      </QueryState>
    </RequirePermission>
  );
}

function RateCardForm({ env, scale, scaleStatus, onClose }: {
  env: string;
  scale: number | null;
  scaleStatus: Parameters<typeof scaleUnavailableReason>[0];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [unit, setUnit] = useState('token');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [margin, setMargin] = useState('');
  const [minimum, setMinimum] = useState('');
  const [reason, setReason] = useState('');

  const inputCost = parseDecimal(input);
  const outputCost = parseDecimal(output);
  const marginBps = percentToBps(margin);
  const min = parseDisplayedCredits(minimum, scale);
  const errors = {
    input: input !== '' && inputCost === null ? 'A non-negative number.' : null,
    output: output !== '' && outputCost === null ? 'A non-negative number.' : null,
    margin: margin !== '' && marginBps === null ? 'A non-negative percentage.' : null,
    minimum: minimum !== '' && !min.ok ? min.error : null,
  };
  const valid = provider.trim() !== '' && model.trim() !== '' && unit.trim() !== '' && inputCost !== null && outputCost !== null &&
    marginBps !== null && min.ok && reasonOk(reason);

  const save = useMutation({
    mutationFn: () => aiApi.addRateCard(env, {
      provider_key: provider.trim(),
      model_key: model.trim(),
      unit: unit.trim(),
      input_cost_per_million_usd: inputCost!,
      output_cost_per_million_usd: outputCost!,
      margin_bps: marginBps!,
      min_charge_credits: min.ok ? min.micro : 0,
      reason: reason.trim(),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: aiKey(env) }); onClose(); },
  });

  return (
    <Modal title="Add a rate-card version" size="lg" onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Add version'}</button>
        </>
      }>
      <p className="t-caption muted" style={{ margin: 0 }}>The new version is active at once; the current one for this provider and model is deactivated with it.</p>
      <div className="form-grid">
        <TextField label="Provider" value={provider} onChange={setProvider} mono placeholder="openai" />
        <TextField label="Model" value={model} onChange={setModel} mono placeholder="gpt-4o-mini" />
        <TextField label="Unit" value={unit} onChange={setUnit} mono hint="token unless the provider bills otherwise." />
        <TextField label="Input cost (USD per million)" value={input} onChange={setInput} error={errors.input} />
        <TextField label="Output cost (USD per million)" value={output} onChange={setOutput} error={errors.output} />
        <TextField label="Margin (%)" value={margin} onChange={setMargin} error={errors.margin}
          hint={marginBps !== null ? `${marginBps} bps over our cost.` : 'Required — 0 sells at cost.'} />
        <TextField label="Minimum charge (credits)" value={minimum} onChange={setMinimum} error={errors.minimum}
          disabled={!isValidScale(scale)}
          hint={!isValidScale(scale) ? scaleUnavailableReason(scaleStatus) : min.ok ? `${min.micro.toLocaleString('en-US')} micro-credits per call.` : 'Per call, in displayed credits.'} />
      </div>
      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={save.error} service="ai" />
    </Modal>
  );
}

function TariffForm({ env, scale, scaleStatus, onClose }: {
  env: string;
  scale: number | null;
  scaleStatus: Parameters<typeof scaleUnavailableReason>[0];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [task, setTask] = useState('');
  const [unit, setUnit] = useState('request');
  const [rate, setRate] = useState('');
  const [minimum, setMinimum] = useState('');
  const [reason, setReason] = useState('');

  const perUnit = displayedRateToMicro(rate, scale);
  const min = parseDisplayedCredits(minimum, scale);
  const valid = task.trim() !== '' && unit.trim() !== '' && perUnit !== null && min.ok && reasonOk(reason);
  const save = useMutation({
    mutationFn: () => aiApi.addLocalTariff(env, {
      task: task.trim(), unit: unit.trim(), credits_per_unit: perUnit!, min_charge_credits: min.ok ? min.micro : 0, reason: reason.trim(),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: aiKey(env) }); onClose(); },
  });

  return (
    <Modal title="Add a local-tariff version" onClose={save.isPending ? undefined : onClose} dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Add version'}</button>
        </>
      }>
      <div className="form-grid">
        <TextField label="Task" value={task} onChange={setTask} mono placeholder="translation" />
        <TextField label="Unit" value={unit} onChange={setUnit} mono />
        <TextField label="Credits per unit" value={rate} onChange={setRate} disabled={!isValidScale(scale)}
          error={rate !== '' && perUnit === null && isValidScale(scale) ? 'A non-negative number.' : null}
          hint={!isValidScale(scale) ? scaleUnavailableReason(scaleStatus) : perUnit !== null ? `${perUnit.toLocaleString('en-US', { maximumFractionDigits: 6 })} micro-credits per ${unit || 'unit'}.` : 'In displayed credits; fractions allowed.'} />
        <TextField label="Minimum charge (credits)" value={minimum} onChange={setMinimum} disabled={!isValidScale(scale)}
          error={minimum !== '' && !min.ok ? min.error : null}
          hint={min.ok ? `${min.micro.toLocaleString('en-US')} micro-credits.` : 'Per call, in displayed credits.'} />
      </div>
      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={save.error} service="ai" />
    </Modal>
  );
}

function DeactivateDialog({ env, target, onClose }: {
  env: string;
  target: { kind: 'card'; row: AiRateCard } | { kind: 'tariff'; row: AiLocalTariff };
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const what = target.kind === 'card' ? `${target.row.provider_key} ${target.row.model_key}` : target.row.task;
  const save = useMutation({
    mutationFn: (): Promise<unknown> => target.kind === 'card'
      ? aiApi.deactivateRateCard(env, target.row.id, reason.trim())
      : aiApi.deactivateLocalTariff(env, target.row.id, reason.trim()),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: aiKey(env) }); onClose(); },
  });
  return (
    <ConfirmDialog title={`Deactivate ${what}?`} confirmLabel="Deactivate" destructive pending={save.isPending}
      confirmDisabled={!reasonOk(reason)} onConfirm={() => save.mutate()} onCancel={() => { save.reset(); onClose(); }}>
      <p className="t-body" style={{ margin: 0 }}>
        {target.kind === 'card'
          ? 'Calls to this model are then priced from the last known card or refused until a new version is added.'
          : 'This task is then priced from the cloud card or refused until a new version is added.'}
      </p>
      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={save.error} service="ai" />
    </ConfirmDialog>
  );
}
